import propsStore from '../../../../../../../store/propsStore/propsStore';
import { IApiaForm } from '../../../../../../../JSApi/types';
import { TButtonsInfo } from '../../types';
import ApiaApi, { makeApiaUrl } from '../../../../../../../api/apiaApi/apiaApi';
import { executionFloatingChatController } from '../..';
import { validationsStore } from '@apia/validations';
import { capture } from './capture';
import { getLabel } from '@apia/util';
import { parseMarkdown } from '@apia/theme';
import { needComment } from '../forms/runPromptForms';
import TabsController from '../../../../../../common/tabs/TabsController';
import { getFormsAssistantFormName } from '../../getFormsAssistantFormName';
import { getFormsAssistantConfiguration } from '../../getFormsAssistantConfiguration';
import { ChatMessage, TMessageType } from '@apia/ai';

declare global {
  interface Window {
    IS_FIRST_TASK: boolean;
    CURRENT_BUS_ENT_NAME: string;
  }
}
function isEffectivelyEmpty(el: HTMLElement): boolean {
  // Get all child nodes that are not just whitespace text
  for (const child of Array.from(el.childNodes)) {
    if (child.nodeType === Node.TEXT_NODE) {
      if (child.textContent?.trim()) {
        return false; // has text
      }
    } else if (child.nodeType === Node.ELEMENT_NODE) {
      const childEl = child as HTMLElement;

      // If element has attributes beyond class (and not just auto-generated) it counts
      const attrNames = Array.from(childEl.attributes).map((a) => a.name);
      if (attrNames.some((a) => a !== 'class')) {
        return false;
      }

      // If element has non-empty class AND might have styling, you may want to count it
      if (childEl.className.trim() && childEl.textContent?.trim()) {
        return false;
      }

      // Recurse: check if this child is itself effectively empty
      if (!isEffectivelyEmpty(childEl)) {
        return false;
      }
    }
  }
  return true;
}

async function waitCondition(cond: () => boolean, ms: number) {
  return new Promise<void>((resolve) => {
    const interval = setInterval(() => {
      if (cond()) {
        resolve();
        clearInterval(interval);
      }
    }, ms);
  });
}

export const generateUserManual = async () => {
  const apiaFunctions = window.ApiaFunctions;

  const forms: IApiaForm[] = apiaFunctions.getAllForms();
  let formsIds: string = '';
  let formsNames: string = '';
  forms.forEach((form: IApiaForm, idx: number) => {
    if (!form.frmId) throw new Error("There's no form id");

    const formProps = propsStore.getFieldProps(form.frmId);
    formProps.formData; // es la data del xml
    if (
      formProps.frmHidden ||
      window.getComputedStyle(
        form.formElement.closest('.tabs__render__child') as HTMLElement,
      ).display === 'none'
    )
      return;
    formsIds +=
      form.frmId.split(/_/, 2)[1] + (idx !== forms.length - 1 ? ',' : '');
    formsNames += form.name + (idx !== forms.length - 1 ? ',' : '');
  });

  if (formsIds === '') {
    return executionFloatingChatController.addMessage(
      new ChatMessage(
        getLabel('msgAiManualFormsError')?.text ?? 'Error while loading label',
        'error' as TMessageType,
      ),
    );
  }

  executionFloatingChatController.addMessage(
    new ChatMessage(
      getLabel('msgAiManualGeneration').text,
      'information' as TMessageType,
    ),
  );
  const formsAssistantConfigurations = getFormsAssistantConfiguration();
  const forceDocumentProcessFlow = validationsStore.getFieldStore(
    getFormsAssistantFormName(),
    formsAssistantConfigurations.FORCE_DOCUMENT_PROCESS_FLOW
      ? formsAssistantConfigurations.FORCE_DOCUMENT_PROCESS_FLOW.name
      : '',
  )?.value;

  const buttonsArray: HTMLElement[] = [
    ...document.querySelectorAll('button.actionButton'),
  ] as HTMLElement[];

  const flowInfo: Record<string, object> = {};

  const flowElementDiv = (
    !window.IS_FIRST_TASK
      ? document.querySelector('.execution__taskMonitor')
      : null
  ) as HTMLElement;
  if (flowElementDiv) {
    const flowElementTable = flowElementDiv.querySelector(
      'table',
    ) as HTMLTableElement;
    const flowElementTBody = flowElementTable.querySelector(
      'tbody',
    ) as HTMLTableSectionElement;

    if (flowElementTBody?.firstElementChild?.children.length !== 1) {
      const flowTableHeadersInfo = [
        ...flowElementTable.querySelectorAll('th'),
      ].map((x) => {
        return {
          id: x.innerText.replace(/ /g, '__').trim(),
          label: x.innerText,
        };
      });

      flowInfo['tableHeadInfo'] = [flowTableHeadersInfo];

      const flowBodyRows = [...flowElementTBody.querySelectorAll('tr')].reduce(
        (flowBodyRows, x, idx) => {
          flowBodyRows.push(
            ([...x.children] as HTMLTableCellElement[]).map((y, jdx) => {
              return {
                label: y.innerText,
                id: `${idx}_-_${flowTableHeadersInfo[jdx].label}`,
              };
            }),
          );
          return flowBodyRows;
        },
        [] as { label: string; id: string }[][],
      );
      flowInfo['tableBodyInfo'] = flowBodyRows;
    }
  }
  let tabsInfo = null;
  if (
    window.TabsController.tabsArray.length &&
    document.querySelector('div.tabsList')
  ) {
    tabsInfo = window.TabsController.tabsArray.reduce(
      (
        taskInfo: Record<
          string,
          { id: string | number; label: string; tooltip?: string }
        >,
        x,
      ) => {
        taskInfo[x.id] = { id: x.id, label: x.label, tooltip: x.tooltip };
        return taskInfo;
      },
      {},
    );
    if (tabsInfo.documents) {
      const hasEntityDoc = document.querySelector(
        "div[data-testid='EntityUploader']",
      );
      const label = tabsInfo.documents.label;
      delete tabsInfo.documents;
      let id = null;
      const hasProcessDoc = document.querySelector('.processData__actions');
      if (hasEntityDoc && hasProcessDoc) id = 'bothDocuments';
      else if (hasEntityDoc) id = 'entityDocuments';
      else if (hasProcessDoc) id = 'processDocuments';
      else id = 'documents';
      tabsInfo = Object.values(tabsInfo);
      tabsInfo.push({ id, label });
    } else {
      tabsInfo = Object.values(tabsInfo);
    }
    // cambiar por diccionario de diccionarios para poder
    // tomar el que tenga id document y formar los objetos necesarios dependiendo de los documentos
    // qe se necesiten i.e. proceso y entidad
  }

  const visibilitiesTitle =
    document.getElementsByClassName('visibilitiesTitle').length > 0;
  const categoriesTitle =
    document.getElementsByClassName('categoriesTitle').length > 0;

  const dynamicHeaders = {
    ...(visibilitiesTitle ? { visibilitiesTitle: true } : {}),
    ...(categoriesTitle ? { categoriesTitle: true } : {}),
    ...(tabsInfo
      ? {
          tabsInfo: JSON.stringify(tabsInfo),
        }
      : {}),
    ...(buttonsArray.length
      ? {
          buttonsInfo: JSON.stringify(
            Object.values(
              buttonsArray.reduce(
                (ret: Record<string, TButtonsInfo>, x: HTMLElement) => {
                  if (!ret[x.innerText])
                    ret[x.innerText] = {
                      id: (x.dataset.id as string) ?? (x.id as string),
                      label: x.innerText,
                      tooltip: x.getAttribute('aria-label') as string,
                    };
                  return ret;
                },
                {},
              ),
            ),
          ),
        }
      : {}),
    ...(flowInfo && Object.keys(flowInfo.tableBodyInfo ?? {}).length > 0
      ? {
          flowInfo: JSON.stringify(flowInfo),
          needsTableImage: true,
          allowDocumentProcessFlow: false,
        }
      : {
          needsTableImage: false,
          allowDocumentProcessFlow: false,
        }),
  };
  if (forceDocumentProcessFlow && Object.keys(flowInfo).length === 0)
    dynamicHeaders.allowDocumentProcessFlow = true;
  const result = await ApiaApi.post<{
    json: string;
    HTML: string;
  }>(
    makeApiaUrl({
      action: 'getTaskAnalysis',
      ajaxUrl: 'apia.execution.FormAction.run',
      frmParent: 'E',
      ...dynamicHeaders,
    }),
    {
      postData: {
        hasObservations: needComment(),
        frmIds: formsIds,
      },
      postDataTreatement: 'stringify',
    },
  );
  const parsedResponse: {
    excelLabels: Uint8Array;
    document: string;
    message: string;
    type: string;
  } = JSON.parse(result?.data?.json ?? '');

  const currentTaskName = apiaFunctions.getCurrentTaskName() as string;
  const currentProcessName = apiaFunctions.getCurrentProcessName() as string;
  const currentStep = apiaFunctions.getCurrentStep();

  if (parsedResponse && parsedResponse.document !== '') {
    const fileName: string =
      (currentProcessName && currentTaskName
        ? `${currentProcessName}___${currentTaskName}`
        : `${window.CURRENT_BUS_ENT_NAME}___`) +
      (isNaN(currentStep as any) ? '' : `___${currentStep}`);

    // Create a new instance of JSZip
    const zip = new (await import('jszip')).default();
    const isFolderNull = zip.folder(fileName);
    const folder = !isFolderNull ? zip : isFolderNull;

    // Add the in-memory file to the ZIP archive
    folder.file(`${fileName}.md`, parsedResponse.document);
    folder.file(
      `${fileName}.html`,
      await parseMarkdown({
        content: parsedResponse.document,
        title: getLabel('lblUserManual').text,
        tocProps: { title: getLabel('lblContent').text },
      }),
    );

    const body = document.body;
    const width = body.style.width;
    body.style.width = 'fit-content';
    const originalY = window.scrollY;
    const originalX = window.scrollX;
    window.scrollTo(0, 0);
    const formAssistant = body.querySelector(
      '#floatingAssistant',
    ) as HTMLElement;
    if (!formAssistant)
      throw new Error("there's no element with floatingAssistant as id");
    const display = formAssistant.style.display;
    formAssistant.style.display = 'none';
    const originalOverflow = body.style.overflow;

    // Add form images files to the ZIP archive
    for (const formName of formsNames.split(',')) {
      folder.file(
        `${formName}.jpg`,
        await capture(executionFloatingChatController, formName).catch(
          (err) => {
            // Handle errors if any
            formAssistant.style.display = display;
            body.style.overflow = originalOverflow;
            window.scrollTo(originalX, originalY);
            executionFloatingChatController.addMessage(
              new ChatMessage(
                getLabel('msgAiManualGenerationZipError').text,
                'response' as TMessageType,
              ),
            );
            throw new Error(`Error generating Image file: ${err}`);
          },
        ),
      );
    }
    const assistantModal = document.querySelector(
      'div[data-variant="layout.common.modals.overlay"]',
    ) as HTMLDivElement;
    const prevDisplay: {
      assistantModal: string | undefined;
      assistantButton: string | undefined;
    } = { assistantModal: undefined, assistantButton: undefined };
    if (assistantModal) {
      prevDisplay.assistantModal = assistantModal.style.display;
      assistantModal.style.display = 'none';
    }
    const assistantButton = document.querySelector(
      `div[data-variant="layout.ai.execution.formsAssistant.openTextarea"]`,
    ) as HTMLDivElement;
    if (assistantButton) {
      prevDisplay.assistantButton = assistantButton.style.display;
      assistantButton.style.display = 'none';
    }
    const mainAside = document.getElementById('MainAside')
      ?.firstElementChild as HTMLElement;
    if (mainAside) {
      const tabs = mainAside.querySelector('div.tabsList') as HTMLElement;
      const buttons = mainAside.querySelectorAll('fieldset');

      let prevStyle = '';
      if (tabs) {
        prevStyle = tabs.style.display;
        tabs.style.display = 'none';
      }
      if (buttons)
        folder.file(
          'buttonsImage.jpg',
          await capture(executionFloatingChatController, mainAside),
        );

      if (tabs) tabs.style.display = prevStyle;

      const prevStyles: string[] = [];
      if (buttons)
        buttons.forEach((x) => {
          prevStyles.push(x.style.display);
          // eslint-disable-next-line no-param-reassign
          x.style.display = 'none';
        });

      if (tabs)
        folder.file(
          'tabsImage.jpg',
          await capture(executionFloatingChatController, mainAside).catch(
            (err) => {
              formAssistant.style.display = display;
              body.style.overflow = originalOverflow;
              window.scrollTo(originalX, originalY);
              executionFloatingChatController.addMessage(
                new ChatMessage(
                  getLabel('msgAiManualGenerationZipError').text,
                  'response' as TMessageType,
                ),
              );
              throw new Error(`Error generating Image file: ${err}`);
            },
          ),
        );

      if (buttons)
        buttons.forEach((x, idx) => {
          // eslint-disable-next-line no-param-reassign
          x.style.display = prevStyles[idx];
        });
    } else {
      const buttonBar = document.querySelector('.buttonBar') as HTMLElement;
      if (buttonBar) {
        const width = buttonBar.style.width;
        buttonBar.style.width = 'fit-content';
        folder.file(
          'buttonsImage.jpg',
          await capture(executionFloatingChatController, buttonBar).catch(
            (err) => {
              formAssistant.style.display = display;
              body.style.overflow = originalOverflow;
              window.scrollTo(originalX, originalY);
              executionFloatingChatController.addMessage(
                new ChatMessage(
                  getLabel('msgAiManualGenerationZipError').text,
                  'response' as TMessageType,
                ),
              );
              throw new Error(`Error generating Image file: ${err}`);
            },
          ),
        );
        buttonBar.style.width = width;
      }
    }
    const prevTabsInfo: (string | number)[] = TabsController.open;
    if (tabsInfo !== null) {
      const TOK_REPLACE = 'TOK_REPLACE';
      const dataIdPlaceholder = `div[data-testid='tabRender__${TOK_REPLACE}']`;
      const dataTestIdMap: Record<string, string> = {
        bothDocuments: 'documents',
        processDocuments: 'documents',
        entityDocuments: 'documents',
      };
      for (const x of tabsInfo) {
        if ('processForms' === x.id || 'entityForms' === x.id) continue;
        TabsController.openTab(x.id, false);
        let actualId = dataIdPlaceholder.replace(TOK_REPLACE, String(x.id));
        let tabElement = document.querySelector(actualId) as HTMLElement;
        if (tabElement == null) {
          const possibleId = dataTestIdMap[String(x.id)];
          if (possibleId) {
            TabsController.openTab(possibleId, false);
            actualId = dataIdPlaceholder.replace(TOK_REPLACE, possibleId);
            tabElement = document.querySelector(actualId) as HTMLElement;
          }
          if (tabElement == null) continue;
        }
        if (isEffectivelyEmpty(tabElement)) {
          continue;
        }
        await waitCondition(() => {
          const elementRendering = document.querySelector(
            actualId,
          ) as HTMLElement;
          return elementRendering.checkVisibility();
        }, 500);
        const imgName = `${x.id}.jpg`;
        folder.file(
          imgName,
          await capture(executionFloatingChatController, tabElement).catch(
            (err) => {
              formAssistant.style.display = display;
              body.style.overflow = originalOverflow;
              window.scrollTo(originalX, originalY);
              executionFloatingChatController.addMessage(
                new ChatMessage(
                  getLabel('msgAiManualGenerationZipError').text,
                  'response' as TMessageType,
                ),
              );
              throw new Error(
                `Error generating Image file: ${imgName} err: ${err}`,
              );
            },
          ),
        );
      }
    }

    // Add the xls file to the ZIP archive
    folder.file(`${fileName}.xls`, parsedResponse.excelLabels);
    document.body.style.width = width;
    let isFirst = true;
    const windowFrame: Blob = await capture(
      executionFloatingChatController,
    ).catch((err) => {
      // Handle errors if any
      formAssistant.style.display = display;
      body.style.overflow = originalOverflow;
      window.scrollTo(originalX, originalY);
      executionFloatingChatController.addMessage(
        new ChatMessage(
          getLabel('msgAiManualGenerationZipError').text,
          'response' as TMessageType,
        ),
      );
      throw new Error(`Error generating Image file: ${err}`);
    });
    if (assistantButton) {
      assistantButton.style.display = prevDisplay.assistantButton as string;
    }
    if (assistantModal) {
      assistantModal.style.display = prevDisplay.assistantModal as string;
    }
    // Add the image file to the ZIP archive
    folder.file(`${fileName}.jpg`, windowFrame);
    prevTabsInfo.forEach((key) => {
      TabsController.openTab(key, isFirst);
      isFirst = false;
    });
    // Generate the ZIP file asynchronously
    zip
      .generateAsync({ type: 'blob' })
      .then((content) => {
        const link = document.createElement('a');
        link.href = URL.createObjectURL(content);
        link.download = `${fileName}.zip`;

        link.click();

        executionFloatingChatController.addMessage(
          new ChatMessage(
            parsedResponse.message,
            parsedResponse.type as TMessageType,
          ),
        );

        // Clean up resources
        URL.revokeObjectURL(link.href);
      })
      .catch((err) => {
        // Handle errors if any
        executionFloatingChatController.addMessage(
          new ChatMessage(
            getLabel('msgAiManualGenerationZipError').text,
            'response' as TMessageType,
          ),
        );

        throw new Error(`Error generating ZIP file: ${err}`);
      })
      .finally(() => {
        formAssistant.style.display = display;
        body.style.overflow = originalOverflow;
        window.scrollTo(originalX, originalY);
      });
  }
};
